import {
  type FC,
  useEffect,
  useMemo,
  useRef
} from 'react'

import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

/* import { IconSwipeUp } from 'assets/svg/sprite'
 */import { PreviewBackgroundGradient } from 'components-ref/atoms'
import { LastPostInfo } from 'components-ref/molecules'
import ImagePreview from 'features/Preview/ImagePreview'
import { useOrientation } from 'helpers/helperHooks'
import { useLongPressFullscreenImage } from 'helpers/hooks/useLongPressFullscreenImage'
import { getCachedBlob } from 'helpers/mediaPrefetch'
import { renderPostText } from 'helpers/postHelpers'
import { type IFile } from 'types/interfaces/IFile'
import { type IPostFile } from 'types/interfaces/ITypes'

interface Props {
  mediaItem: IFile
  postFile: IPostFile
  minimized: boolean
  isFirstPost: boolean
  toggleControls: () => void
  showControls: boolean
  hasNextPage?: boolean
  isLastPost?: boolean
  isInViewport?: boolean
  onOpenOptions?: () => void
  isReel?: boolean
}

const SinglePostPreviewImage: FC<Props> = ({
  mediaItem,
  postFile,
  minimized,
  showControls,
  toggleControls,
  hasNextPage,
  isLastPost = false,
  onOpenOptions,
  isReel = false
}) => {
  const deviceOrientation = useOrientation()
  const { t } = useTranslation()

  const isPortrait =
    deviceOrientation?.type?.includes('portrait') ?? true

  const orientation = mediaItem.orientation
    ? mediaItem.orientation.toLowerCase()
    : ''
const pressLayerRef = useRef<HTMLDivElement | null>(null)

useEffect(() => {
  if (showControls) return

  const root = pressLayerRef.current
  if (!root) return

  const zoomContainer = root.querySelector(
    '.swiper-zoom-container'
  ) as HTMLElement | null

  const image = zoomContainer?.querySelector(
    '.preview__file__image'
  ) as HTMLImageElement | null

  if (!zoomContainer || !image) return

  let clamping = false

  const clamp = () => {
    if (clamping) return

    const slide = root.closest(
      '.swiper-slide'
    ) as HTMLElement | null

    if (!slide) return

    const imageTransform = new DOMMatrix(
      getComputedStyle(image).transform
    )

    const scale = imageTransform.a || 1

    if (scale <= 1) return

    const transform = new DOMMatrix(
      getComputedStyle(zoomContainer).transform
    )

    const scaledWidth =
      image.offsetWidth * scale

    const scaledHeight =
      image.offsetHeight * scale

    const maxX = Math.max(
      0,
      (scaledWidth - slide.clientWidth) / 2
    )

    const maxY = Math.max(
      0,
      (scaledHeight - slide.clientHeight) / 2
    )

    const x = Math.max(
      -maxX,
      Math.min(maxX, transform.m41)
    )

    const y = Math.max(
      -maxY,
      Math.min(maxY, transform.m42)
    )

    if (
      x === transform.m41 &&
      y === transform.m42
    ) {
      return
    }

    clamping = true

    zoomContainer.style.transform =
      `translate3d(${x}px, ${y}px, 0px)`

    clamping = false
  }

  const observer = new MutationObserver(() => {
    clamp()
  })

  observer.observe(zoomContainer, {
    attributes: true,
    attributeFilter: ['style']
  })

  return () => {
    observer.disconnect()
  }
}, [showControls])
  /**
   * Smaller image used for:
   * - blurred/background image
   * - minimized preview
   * - fallback if the full image is unavailable
   */
  const thumbnailUrl = useMemo(() => {
    return (
      mediaItem.thumb_url ??
      mediaItem.active_thumb ??
      mediaItem.thumbnail_src ??
      mediaItem.url
    )
  }, [
    mediaItem.thumb_url,
    mediaItem.active_thumb,
    mediaItem.thumbnail_src,
    mediaItem.url
  ])

  /**
   * Full image used in the foreground.
   */
  const fullImageUrl = mediaItem.url ?? thumbnailUrl

  /**
   * This is intentionally read synchronously during render.
   *
   * If PostViewWrapper already prefetched the image, the first rendered
   * foreground src will be the blob URL instead of rendering the signed URL
   * first and replacing it afterward.
   */
const cachedThumbnail = thumbnailUrl
  ? getCachedBlob(thumbnailUrl)
  : null

const cachedFullImage = fullImageUrl
  ? getCachedBlob(fullImageUrl)
  : null

const backgroundUrl =
  cachedThumbnail ??
  thumbnailUrl

const foregroundUrl =
  cachedFullImage ??
  fullImageUrl ??
  backgroundUrl

const longPressHandlers = useLongPressFullscreenImage({
  delay: 500,
  moveThreshold: 12,

  onLongPress: () => {
    if (showControls) {
      onOpenOptions?.()
    }
  },

  onClick: () => {
    toggleControls()
  }
})

  return (
    <div className={`preview__file ${orientation}`}>
      {!minimized && <PreviewBackgroundGradient />}

      {minimized ? (
        <div className="preview__minimized">
          <img
            className="preview__file__image preview__file__image--minimized"
            src={thumbnailUrl}
            alt={t('preview')}
            onLoad={event => {
              const image = event.currentTarget

              console.log('[PREVIEW_MINIMIZED_IMAGE_LOADED]', {
                mediaId: mediaItem.id,
                src: thumbnailUrl,
                currentSrc: image.currentSrc,
                naturalWidth: image.naturalWidth,
                naturalHeight: image.naturalHeight,
                usingBlob: image.currentSrc.startsWith('blob:')
              })
            }}
            onError={event => {
              console.log('[PREVIEW_MINIMIZED_IMAGE_ERROR]', {
                mediaId: mediaItem.id,
                src: thumbnailUrl,
                currentSrc: event.currentTarget.currentSrc
              })
            }}
          />

          <div className="preview__minimized__content">
            <div className="preview__minimized__content--top">
              <div className="header__avatar">
                <img
                  src={postFile.minimizedAvatar}
                  alt={t('placeholder')}
                />
              </div>

              <div className="header__name">
                {postFile.minimizedName}
              </div>
            </div>

            <div className="preview__minimized__content--bottom">
              <div className="content__text">
                {renderPostText(
                  postFile.text ?? '',
                  postFile.mentions
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <MediaContainer>
          <PressLayer
  ref={pressLayerRef}
  $zoomMode={!showControls}
  {...longPressHandlers}
>
            <div
              className="preview__background"
              style={{
                backgroundImage: backgroundUrl
                  ? `url(${backgroundUrl})`
                  : undefined
              }}
            />

            <ImagePreview
              src={foregroundUrl}
              textObject={mediaItem.text}
            />
          </PressLayer>

{/*           {shouldShowFirstTourGuide() && (
            <div className="preview__swipe--icon">
              <IconSwipeUp
                opacity={
                  isPortrait
                    ? orientation === 'landscape'
                      ? 0.5
                      : 1
                    : 1
                }
              />

              <p
                style={{
                  opacity: isPortrait
                    ? orientation === 'landscape'
                      ? 0.5
                      : 1
                    : 1
                }}
              >
                {t('swipeUpForNextPost')}
              </p>
            </div>
          )} */}
        </MediaContainer>
      )}

      {isLastPost && hasNextPage === false && (
        <LastPostInfo isReel={isReel} />
      )}
    </div>
  )
}

export default SinglePostPreviewImage

const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: black;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
`

const PressLayer = styled.div<{ $zoomMode: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  ${({ $zoomMode }) =>
    $zoomMode &&
    `
      touch-action: none;

      .swiper-zoom-container,
      .preview__file__image {
        touch-action: none;
      }
    `}
`